import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Error as ErrorIcon } from '@mui/icons-material';
import { ListItemText, Tooltip } from '@mui/material';

import {
  type CareSettingTerminology,
  getCareSettingTerminologyForFacilityType
} from '@/hooks/facility-selection/careSettingTerminology';
import { getFacilityTypeGroupOptions } from '@/hooks/facility-selection/screenerFacilityTypeHelpers';
import { useFacilitySelectorController } from '@/hooks/facility-selection/useFacilitySelectorController';
import { useScreenerFacilityView } from '@/hooks/facility-selection/useScreenerFacilityView';
import { FacilityPayload } from '@/hooks/useFacilitiesQuery';
import useIsScreenerAllFacilitiesSelected, {
  SCREENER_ROUTES_WITH_ALL_FACILITIES_SUPPORT
} from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useIsScreenerAllFacilitiesSelected';
import { RoutePath } from '@/routes/RoutePath';

import { FacilitySelectorListItemButton } from './FacilitySelectorPopover';

interface CrmScreenerSelectAllButtonProps {
  setIsOpen: (value: React.SetStateAction<boolean>) => void;
  hide: boolean;
  billingDisabledFacilityIds: ReadonlySet<string>;
  facilities: FacilityPayload[];
  accessibleFacilityIds: ReadonlySet<string>;
}

export default function CrmScreenerSelectAllButton({
  hide = false,
  setIsOpen,
  billingDisabledFacilityIds,
  facilities,
  accessibleFacilityIds
}: CrmScreenerSelectAllButtonProps) {
  const location = useLocation();
  const shouldShowSelectAllButton =
    SCREENER_ROUTES_WITH_ALL_FACILITIES_SUPPORT.includes(
      location.pathname as RoutePath
    );
  const { view } = useScreenerFacilityView();
  const isAllFacilitiesSelected = useIsScreenerAllFacilitiesSelected();
  const { selectAllFacilities } = useFacilitySelectorController();

  const groupOptions = useMemo(
    () => getFacilityTypeGroupOptions(facilities, accessibleFacilityIds),
    [facilities, accessibleFacilityIds]
  );

  // "All" is only useful when there's more than one facility to aggregate
  const selectableGroups = groupOptions.filter(
    (group) => group.facilityIds.length > 1
  );

  if (!shouldShowSelectAllButton || hide || selectableGroups.length === 0) {
    return null;
  }

  const activeFacilityType =
    view.kind === 'all-facilities' ? view.facilityType : null;
  const isMultiType = groupOptions.length > 1;

  return (
    <>
      {selectableGroups.map((group) => {
        const isGroupBillingDisabled = group.facilityIds.some((facilityId) =>
          billingDisabledFacilityIds.has(facilityId)
        );

        return (
          <FacilitySelectorListItemButton
            key={group.facilityType}
            role="option"
            selected={
              isAllFacilitiesSelected &&
              activeFacilityType === group.facilityType
            }
            disabled={isGroupBillingDisabled}
            onClick={() => {
              selectAllFacilities(group.facilityType);
              setIsOpen(false);
            }}
            sx={isGroupBillingDisabled ? { opacity: 0.5 } : undefined}
          >
            <ListItemText
              primary={isMultiType ? group.label : 'Select All'}
              secondary={isMultiType ? group.subtitle : undefined}
            />
            {isGroupBillingDisabled && (
              <BillingDisabledTooltip
                careSettingTerms={getCareSettingTerminologyForFacilityType(
                  group.facilityType
                )}
              />
            )}
          </FacilitySelectorListItemButton>
        );
      })}
    </>
  );
}

const BillingDisabledTooltip = ({
  careSettingTerms
}: {
  careSettingTerms: CareSettingTerminology;
}) => (
  <Tooltip
    title={`All ${careSettingTerms.pluralTitle} view is disabled due to an unpaid balance on one or more ${careSettingTerms.pluralLower}.`}
    placement="top"
  >
    <ErrorIcon
      sx={{
        fontSize: 18,
        color: '#D32F2F !important',
        ml: 1,
        pointerEvents: 'auto'
      }}
    />
  </Tooltip>
);
